

import React from 'react';
import { StatCardWidget } from '../widgets';
import { CalendarDays, CalendarRange, MessageSquare, CheckSquare, IndianRupee, TrendingUp, TrendingDown, HandCoins, AlertTriangle } from 'lucide-react';
import { formatCurrency } from '@/lib/data';
import { plural, type DashboardData } from '../../useDashboard';

interface StatsSectionProps {
  data: DashboardData;
}

/**
 * Headline tiles. Every tile says what it counts; cancelled events never count.
 * Money tiles only appear when the server sent money figures (super_admin / admin / master).
 */
export function StatsSection({ data }: StatsSectionProps) {
  const { counts, money, sections } = data;
  const overdueTasks = counts.overdue_tasks ?? 0;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4">
        <StatCardWidget
          title="Today"
          value={plural(counts.today_events, 'event')}
          subtitle={counts.today_events > 0 ? `${counts.today_guests.toLocaleString('en-IN')} guests expected` : 'Nothing booked today'}
          icon={<CalendarDays className="w-5 h-5" />}
          color={counts.today_events > 0 ? 'gold' : 'stone'}
          to="/calendar"
        />
        <StatCardWidget
          title="This month"
          value={plural(counts.month_events, 'event')}
          subtitle={`Starting this month · ${counts.month_events_ahead.toLocaleString('en-IN')} still to come`}
          icon={<CalendarRange className="w-5 h-5" />}
          color="blue"
          to="/events"
        />
        {sections.enquiries && counts.open_enquiries !== null && (
          <StatCardWidget
            title="Open enquiries"
            value={counts.open_enquiries.toLocaleString('en-IN')}
            subtitle="New or qualified, not yet booked"
            icon={<MessageSquare className="w-5 h-5" />}
            color="purple"
            to="/enquiries"
          />
        )}
        {sections.tasks && counts.open_tasks !== null && (
          <StatCardWidget
            title="Open tasks"
            value={counts.open_tasks.toLocaleString('en-IN')}
            subtitle={overdueTasks > 0 ? `${overdueTasks} overdue` : 'None overdue'}
            icon={<CheckSquare className="w-5 h-5" />}
            color={overdueTasks > 0 ? 'red' : 'stone'}
            to="/tasks"
          />
        )}
      </div>

      {money && (
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4">
          <StatCardWidget
            title="Revenue this month"
            value={formatCurrency(money.month_revenue)}
            subtitle={`${plural(money.month_event_count, 'event')} starting this month · after discount, before tax`}
            icon={<IndianRupee className="w-5 h-5" />}
            color="gold"
            to="/events"
          />
          <StatCardWidget
            title="Profit this month"
            value={formatCurrency(money.month_profit)}
            valueClassName={money.month_profit < 0 ? 'text-red-700' : ''}
            subtitle={money.month_margin !== null ? `${money.month_margin.toFixed(0)}% of revenue · same events` : 'No events this month yet'}
            icon={money.month_profit < 0 ? <TrendingDown className="w-5 h-5" /> : <TrendingUp className="w-5 h-5" />}
            color={money.month_profit < 0 ? 'red' : 'green'}
            to="/events"
          />
          <StatCardWidget
            title="To collect"
            value={formatCurrency(money.to_collect)}
            subtitle={money.to_collect_events > 0 ? `Still owed on ${plural(money.to_collect_events, 'booked event')}` : 'Nothing owed'}
            icon={<HandCoins className="w-5 h-5" />}
            color="stone"
            to="/events"
          />
          <StatCardWidget
            title="Overdue"
            value={formatCurrency(money.overdue)}
            valueClassName={money.overdue > 0 ? 'text-red-700' : ''}
            subtitle={money.overdue_events > 0 ? `${plural(money.overdue_events, 'event')} already over, not fully paid` : 'Nothing overdue'}
            icon={<AlertTriangle className="w-5 h-5" />}
            color={money.overdue > 0 ? 'red' : 'stone'}
            to="/events"
          />
        </div>
      )}
    </div>
  );
}
